import {
  createContext,
  createEffect,
  createSignal,
  JSXElement,
  mergeProps,
  onCleanup,
  untrack,
  useContext
} from 'solid-js';
import { createStore } from 'solid-js/store';
import { ConnectionData } from '~/api/types/connectionData.ts';
import {
  ERROR_CODES,
  getConnectionList,
  getDirectoriesForOrganization,
  getDirectoryDetailsById,
  getOrganizationById,
  getOrganizationDetailsKey,
  getOrganizationDirectoriesListQueryKey,
  getOrganizationDomains,
  getOrganizationRolesQueryKey,
  getOrganizationRolesWithPermissions,
  getOrganizationsConnectionDetailsKey,
  getOrganizationsConnectionKey,
  getOrganizationsDirectoryDetailsQueryKey,
  getOrganizationsDomainsKey,
  getOrganizationUserManagementSettings,
  getOrgUserManagementSettingsQueryKey,
  getSSOConnectionDetails,
  isApiError,
  OrganizationData,
  OrganizationUserManagementSettings,
  retryAuthorizedRequests
} from '~/api';
import { useNavigate, useParams } from '@solidjs/router';
import { useQuery, useQueryClient } from '@tanstack/solid-query';
import {
  cloneObject,
  isEnvHostedContext,
  withPortalAuthToken
} from '~/utils/commonUtils.ts';
import { HttpStatusCode } from 'axios';
import { getConnectionListUrl } from '~/pages/Authentication/Connections/utils.ts';
import { DirectoryData, DirectorySecret } from '~/api/types/directoryData.ts';
import { OrgDetailsDataType } from '~/components/AppRouter/utils.ts';
import logger from '~/utils/logger';
import { timeSignatures } from '~/consts.ts';
import { getDirectoryListUrl } from '~/pages/DirectorySync/utils.ts';
import { RoleData } from '~/api/types/roleData.ts';
import { DomainData } from '~/api/types/domainData.ts';
import { AppErrors } from '~/components/ErrorBoundaries/utils.ts';

type OrganizationContextProps = {
  children: (...params: any[]) => JSXElement;
  type?: OrgDetailsDataType;
  isTestOrg?: boolean;
  organization?: OrganizationData;
  organizationId?: string;
};

export type OrganizationContextValueType = {
  organization: OrganizationData | null;
  connections: ConnectionData[];
  selectedConnection: ConnectionData | null;
  setSelectedConnection: (c: ConnectionData) => void;
  directories: DirectoryData[];
  selectedDirectory: DirectoryData | null;
  refetchOrganizationDetails: () => Promise<void>;
  refetchConnectionDetails: () => Promise<any>;
  refetchDirectoryDetails: () => Promise<any>;
  refetchOrgRolesData: () => Promise<any>;
  refetchOrgUserManagementSettingsData: () => Promise<any>;
  refetchOrgDomainData: () => Promise<any>;
  addConnectionToList: (c: ConnectionData) => void;
  removeConnectionFromList: (cId: string) => void;
  setConnectionDetailsLoaded: (boolean) => void;
  pollDirectoryDetails: (interval?: number) => void;
  stopPollingDirectoryDetails: () => void;
  addDirectorySecret: (s: DirectorySecret) => void;
  organizationRolesData: RoleData[];
  orgUserManagementSettings: OrganizationUserManagementSettings | null;
  orgDomainData: DomainData[];
  isDataFetched: OrganizationDataLoadedState;
};

type OrganizationDataLoadedState = {
  organizationDetailsQuery: boolean;
  connectionsQuery: boolean;
  connectionDetailsQuery: boolean;
  directoriesQuery: boolean;
  directoriesDetailsQuery: boolean;
  orgRolesListQuery: boolean;
  orgUserManagementSettingsQuery: boolean;
  orgDomainListQuery: boolean;
};

const OrganizationContext = createContext();

const DEFAULT_POLL_INTERVAL = 10 * timeSignatures.SEC_IN_MILLIS;

const shouldFetchConnections = (orgDetailsType: OrgDetailsDataType) => {
  return (
    orgDetailsType === OrgDetailsDataType.SSO_CONNECTIONS ||
    orgDetailsType === OrgDetailsDataType.ENV_SSO_CONNECTIONS
  );
};

const shouldFetchDirectories = (orgDetailsType: OrgDetailsDataType) => {
  return (
    orgDetailsType === OrgDetailsDataType.DIRECTORIES ||
    orgDetailsType === OrgDetailsDataType.ENV_DIRECTORIES
  );
};

const shouldFetchOrgRoles = (orgDetailsType: OrgDetailsDataType) => {
  return orgDetailsType === OrgDetailsDataType.ROLES;
};

const isInOverview = (orgDetailsType: OrgDetailsDataType) => {
  return orgDetailsType === OrgDetailsDataType.OVERVIEW;
};
export default function OrganizationContextProvider(
  props: OrganizationContextProps
): JSXElement {
  const merged = mergeProps(
    {
      type: OrgDetailsDataType.SSO_CONNECTIONS,
      isTestOrg: false
    },
    props
  );

  const params = useParams();
  const navigate = useNavigate();

  const [organizationId, setOrganizationId] = createSignal<string>(
    params.organizationId || merged.organizationId || ''
  );

  const [isDataLoaded, setIsDataLoaded] =
    createStore<OrganizationDataLoadedState>({
      organizationDetailsQuery: !!merged.organization,
      connectionsQuery: !shouldFetchConnections(merged.type),
      connectionDetailsQuery: !shouldFetchConnections(merged.type),
      directoriesQuery: !shouldFetchDirectories(merged.type),
      directoriesDetailsQuery: !shouldFetchDirectories(merged.type),
      orgRolesListQuery: !shouldFetchOrgRoles(merged.type),
      orgUserManagementSettingsQuery: !isInOverview(merged.type),
      orgDomainListQuery: !isInOverview(merged.type) && !merged.isTestOrg
    });

  const queryClient = useQueryClient();

  if (!organizationId() && merged.organization) {
    throw new Error(AppErrors.ORGANIZATION_CONTEXT_MISSING_ORG);
  }

  createEffect(() => {
    const newOrgId = params.organizationId || merged.organizationId || '';
    untrack(() => {
      if (newOrgId !== organizationId()) {
        setOrganizationId(newOrgId);
      }
    });
  });

  const timers: {
    directory: ReturnType<typeof setTimeout> | undefined;
    connection: ReturnType<typeof setTimeout> | undefined;
  } = {
    directory: undefined,
    connection: undefined
  };

  const [contextData, setContextData] =
    createStore<OrganizationContextValueType>({
      organization: null,
      connections: [],
      selectedConnection: null,
      setSelectedConnection: (connection) =>
        setContextData('selectedConnection', connection),
      directories: [],
      organizationRolesData: [],
      orgDomainData: [],
      orgUserManagementSettings: null,
      isDataFetched: isDataLoaded,
      selectedDirectory: null,
      refetchOrganizationDetails: async () => {
        await organizationDetailsQuery.refetch();
      },
      refetchConnectionDetails: async () =>
        await connectionDetailsQuery.refetch(),
      refetchDirectoryDetails: async () => directoryDetailsQuery.refetch(),
      refetchOrgRolesData: async () => await orgRolesListQuery.refetch(),
      refetchOrgUserManagementSettingsData: async () =>
        await orgUserManagementSettingsQuery.refetch(),
      refetchOrgDomainData: async () => await orgDomainListQuery.refetch(),
      addConnectionToList: (connection) => {
        queryClient.setQueryData(connectionsQueryKey, (cachedData) => {
          const connections = [...(cachedData as ConnectionData[]), connection];
          setContextData('connections', connections);
          return connections;
        });
      },
      removeConnectionFromList: (connectionId) => {
        queryClient.setQueryData(connectionsQueryKey, (cachedData) => {
          const connections = [
            ...(cachedData as ConnectionData[]).filter(
              ({ id }) => id !== connectionId
            )
          ];
          setContextData('connections', connections);
          return connections;
        });
      },
      setConnectionDetailsLoaded: (isLoaded) =>
        setIsDataLoaded('connectionDetailsQuery', isLoaded),
      pollDirectoryDetails: (pollInterval: number = DEFAULT_POLL_INTERVAL) => {
        if (timers.directory) {
          contextData.stopPollingDirectoryDetails();
        }
        timers.directory = setInterval(async () => {
          try {
            // ideally we should have called refetchDirectoryDetails in the setInterval
            // but that would trigger a few more side effects
            // to avoid that we are calling the api method directly here
            const directoryDetails = await getDirectoryDetailsById(
              organizationId(),
              params.directoryId
            );
            setContextData('selectedDirectory', {
              ...contextData.selectedDirectory,
              ...directoryDetails
            });
          } catch (e) {
            // silently log the error
            if (isApiError(e) && e.httpStatus === HttpStatusCode.NotFound) {
              contextData.stopPollingDirectoryDetails();
              await directoriesQuery.refetch();
              navigate(withPortalAuthToken(getDirectoryListUrl({ params })));
            }
            logger.error(
              `Failed to poll directory details directoryId :: ${params.directoryId}`
            );
          }
        }, pollInterval);
      },
      stopPollingDirectoryDetails: () => {
        timers.directory && clearInterval(timers.directory);
      },
      addDirectorySecret: (secret: DirectorySecret) => {
        setContextData('selectedDirectory', (directoryDetails) => ({
          ...directoryDetails,
          secrets: [...(directoryDetails?.secrets ?? []), secret]
        }));
      }
    });

  const organizationDetailsQuery = useQuery(() => ({
    queryKey: getOrganizationDetailsKey(organizationId()),
    queryFn: async () => {
      return await getOrganizationById(organizationId());
    },
    retry: retryAuthorizedRequests
  }));

  createEffect(() => {
    if (organizationDetailsQuery.isSuccess) {
      setIsDataLoaded('organizationDetailsQuery', true);
      setContextData('organization', organizationDetailsQuery.data);
    } else if (
      !organizationDetailsQuery.isPending &&
      organizationDetailsQuery.isError
    ) {
      if (
        organizationDetailsQuery.error?.httpStatus ===
        HttpStatusCode.Unauthorized
      ) {
        throw new Error(ERROR_CODES.UNAUTHORIZED);
      } else {
        if (isEnvHostedContext()) {
          setIsDataLoaded('organizationDetailsQuery', true);
        } else {
          throw new Error(ERROR_CODES.GENERIC);
        }
      }
    }
  });

  const connectionsQueryKey = getOrganizationsConnectionKey(organizationId());

  const connectionsQuery = useQuery(() => ({
    queryKey: connectionsQueryKey,
    queryFn: async () => {
      return await getConnectionList(organizationId());
    },
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  createEffect(() => {
    if (connectionsQuery.isSuccess) {
      setIsDataLoaded('connectionsQuery', true);
      setContextData(
        'connections',
        connectionsQuery.data as Array<ConnectionData>
      );
    } else if (!connectionsQuery.isPending && connectionsQuery.isError) {
      if (
        isApiError(connectionsQuery.error) &&
        connectionsQuery.error.httpStatus === HttpStatusCode.Unauthorized
      ) {
        throw new Error(ERROR_CODES.UNAUTHORIZED);
      } else {
        throw new Error(ERROR_CODES.GENERIC);
      }
    }
  });

  const connectionDetailsQuery = useQuery(() => ({
    queryKey: getOrganizationsConnectionDetailsKey(
      organizationId(),
      params.connectionId
    ),
    queryFn: async () => {
      if (params.connectionId) {
        return await getSSOConnectionDetails(
          organizationId(),
          params.connectionId
        );
      }
      return null;
    },
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  createEffect(async () => {
    if (
      params.connectionId &&
      (!contextData.selectedConnection ||
        params.connectionId !== contextData.selectedConnection?.id)
    ) {
      setIsDataLoaded('connectionDetailsQuery', false);
      await connectionDetailsQuery.refetch();
    }
  });

  createEffect(() => {
    if (!connectionDetailsQuery.isPending && connectionDetailsQuery.isSuccess) {
      setIsDataLoaded('connectionDetailsQuery', true);
      // @todo revisit upadting state with the new connection details
      // it was assigned with null to prevent merging of the state
      // there should be a better way to do this
      setContextData('selectedConnection', null);
      setContextData(
        'selectedConnection',
        cloneObject(connectionDetailsQuery.data)
      );
    } else if (
      !connectionDetailsQuery.isPending &&
      connectionDetailsQuery.isError
    ) {
      if (
        isApiError(connectionDetailsQuery.error) &&
        connectionDetailsQuery.error.httpStatus === HttpStatusCode.NotFound
      ) {
        navigate(withPortalAuthToken(getConnectionListUrl({ params })));
        return;
      } else if (
        isApiError(connectionDetailsQuery.error) &&
        connectionDetailsQuery.error.httpStatus === HttpStatusCode.Unauthorized
      ) {
        throw new Error(ERROR_CODES.UNAUTHORIZED);
      }
      throw new Error(ERROR_CODES.GENERIC);
    }
  });

  const directoriesQueryKey =
    getOrganizationDirectoriesListQueryKey(organizationId());

  const directoriesQuery = useQuery(() => ({
    queryKey: directoriesQueryKey,
    queryFn: async () => {
      return await getDirectoriesForOrganization(organizationId());
    },
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  createEffect(() => {
    if (directoriesQuery.isSuccess && directoriesQuery.data) {
      setIsDataLoaded('directoriesQuery', true);
      setContextData(
        'directories',
        directoriesQuery.data as Array<DirectoryData>
      );
    } else if (!directoriesQuery.isPending && directoriesQuery.isError) {
      if (
        isApiError(directoriesQuery.error) &&
        directoriesQuery.error.httpStatus === HttpStatusCode.Unauthorized
      ) {
        throw new Error(ERROR_CODES.UNAUTHORIZED);
      } else {
        throw new Error(ERROR_CODES.GENERIC);
      }
    }
  });

  const directoryDetailsQuery = useQuery(() => ({
    queryKey: getOrganizationsDirectoryDetailsQueryKey(
      organizationId(),
      params.directoryId
    ),
    queryFn: async () => {
      if (params.directoryId) {
        return await getDirectoryDetailsById(
          organizationId(),
          params.directoryId
        );
      }
      return null;
    },
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  createEffect(async () => {
    if (
      params.directoryId &&
      (!contextData.selectedDirectory ||
        params.directoryId !== contextData.selectedDirectory?.id)
    ) {
      untrack(() => {
        setIsDataLoaded('directoriesDetailsQuery', false);
      });
      await directoryDetailsQuery.refetch();
    }
  });

  createEffect(() => {
    if (
      !directoryDetailsQuery.isPending &&
      directoryDetailsQuery.isSuccess &&
      // specifically having undefined instead of checking !!directoryDetailsQuery.data
      // because it can be null in case no directory is configured
      directoryDetailsQuery.data !== undefined
    ) {
      setIsDataLoaded('directoriesDetailsQuery', true);
      // @todo revisit upadting state with the new directory details
      // it was assigned with null to prevent merging of the state
      // there should be a better way to do this
      setContextData('selectedDirectory', null);
      setContextData(
        'selectedDirectory',
        cloneObject(directoryDetailsQuery.data)
      );
    } else if (
      !directoryDetailsQuery.isPending &&
      directoryDetailsQuery.isError
    ) {
      if (
        isApiError(directoryDetailsQuery.error) &&
        directoryDetailsQuery.error.httpStatus === HttpStatusCode.NotFound
      ) {
        // @todo fix url
        navigate(withPortalAuthToken(getDirectoryListUrl({ params })));
        return;
      } else if (
        isApiError(directoryDetailsQuery.error) &&
        directoryDetailsQuery.error.httpStatus === HttpStatusCode.Unauthorized
      ) {
        throw new Error(ERROR_CODES.UNAUTHORIZED);
      }
      throw new Error(ERROR_CODES.GENERIC);
    }
  });

  const orgRolesListQuery = useQuery(() => ({
    queryKey: getOrganizationRolesQueryKey(organizationId()),
    queryFn: async () => {
      return await getOrganizationRolesWithPermissions({
        orgId: organizationId(),
        includeAllPermissions: true
      });
    },
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  createEffect(() => {
    if (orgRolesListQuery.isSuccess) {
      setIsDataLoaded('orgRolesListQuery', true);
      setContextData('organizationRolesData', orgRolesListQuery.data);
    } else if (!orgRolesListQuery.isPending && orgRolesListQuery.isError) {
      if (
        isApiError(orgRolesListQuery.error) &&
        orgRolesListQuery.error.httpStatus === HttpStatusCode.Unauthorized
      ) {
        throw new Error(ERROR_CODES.UNAUTHORIZED);
      } else {
        throw new Error(ERROR_CODES.GENERIC);
      }
    }
  });

  const orgUserManagementSettingsQuery = useQuery(() => ({
    queryKey: getOrgUserManagementSettingsQueryKey(organizationId()),
    queryFn: async () => {
      return await getOrganizationUserManagementSettings(organizationId());
    },
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  createEffect(() => {
    if (orgUserManagementSettingsQuery.isSuccess) {
      setIsDataLoaded('orgUserManagementSettingsQuery', true);
      setContextData(
        'orgUserManagementSettings',
        orgUserManagementSettingsQuery.data
      );
    } else if (
      !orgUserManagementSettingsQuery.isPending &&
      orgUserManagementSettingsQuery.isError
    ) {
      if (
        isApiError(orgUserManagementSettingsQuery.error) &&
        orgUserManagementSettingsQuery.error.httpStatus ===
          HttpStatusCode.Unauthorized
      ) {
        throw new Error(ERROR_CODES.UNAUTHORIZED);
      } else {
        throw new Error(ERROR_CODES.GENERIC);
      }
    }
  });

  const orgDomainListQuery = useQuery(() => ({
    queryKey: getOrganizationsDomainsKey(organizationId()),
    queryFn: async () => {
      return await getOrganizationDomains(organizationId());
    },
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  createEffect(() => {
    if (orgDomainListQuery.isSuccess && orgDomainListQuery.data) {
      untrack(() => {
        setIsDataLoaded('orgDomainListQuery', true);
        setContextData('orgDomainData', orgDomainListQuery.data.domains ?? []);
      });
    } else if (!orgDomainListQuery.isPending && orgDomainListQuery.isError) {
      if (
        isApiError(orgDomainListQuery.error) &&
        orgDomainListQuery.error.httpStatus === HttpStatusCode.Unauthorized
      ) {
        throw new Error(ERROR_CODES.UNAUTHORIZED);
      } else {
        throw new Error(ERROR_CODES.GENERIC);
      }
    }
  });

  createEffect(() => {
    if (merged.type === OrgDetailsDataType.OVERVIEW) {
      orgUserManagementSettingsQuery.refetch();
      orgDomainListQuery.refetch();
    } else if (shouldFetchConnections(merged.type)) {
      isEnvHostedContext() &&
        untrack(() => {
          setIsDataLoaded('connectionsQuery', false);
          setIsDataLoaded('connectionDetailsQuery', false);
        });
      connectionsQuery.refetch();
      connectionDetailsQuery.refetch();
    } else if (shouldFetchDirectories(merged.type)) {
      isEnvHostedContext() &&
        untrack(() => {
          setIsDataLoaded('directoriesQuery', false);
          setIsDataLoaded('directoriesDetailsQuery', false);
        });
      directoriesQuery.refetch();
      directoryDetailsQuery.refetch();
    } else if (merged.type === OrgDetailsDataType.ROLES) {
      orgRolesListQuery.refetch();
    }

    if (merged.isTestOrg) orgDomainListQuery.refetch();
  });

  const isAllDataLoaded = () => {
    if (isEnvHostedContext()) {
      return (
        isDataLoaded.organizationDetailsQuery &&
        isDataLoaded.orgRolesListQuery &&
        isDataLoaded.orgUserManagementSettingsQuery &&
        isDataLoaded.orgDomainListQuery
      );
    }
    return (
      isDataLoaded.organizationDetailsQuery &&
      isDataLoaded.connectionsQuery &&
      isDataLoaded.connectionDetailsQuery &&
      isDataLoaded.directoriesQuery &&
      isDataLoaded.directoriesDetailsQuery &&
      isDataLoaded.orgRolesListQuery &&
      isDataLoaded.orgUserManagementSettingsQuery &&
      isDataLoaded.orgDomainListQuery
    );
  };

  onCleanup(() => {
    contextData.stopPollingDirectoryDetails();
  });

  return (
    <OrganizationContext.Provider value={contextData}>
      {merged.children(isAllDataLoaded())}
    </OrganizationContext.Provider>
  );
}

export const useOrganizationContext = (): OrganizationContextValueType =>
  useContext(OrganizationContext) as OrganizationContextValueType;
